import { FC, useEffect, useMemo, useState } from 'react';
import { SummaryTabBlockChildProps } from '../summary-tab/SummaryTab';
import { usePatient } from '@hooks/usePatient';
import { MedicalHistoryContent } from '../examination/MedicalHistoryContent';
import { LoaderWithChildren } from '@/components/LoaderWithChildren';
import { usePerinatalPeriodResponse } from './hooks/usePerinatalPeriodResponse';
import { usePrenatalPeriodResponse } from './hooks/usePrenatalPeriodResponse';
import BirthPeriodListContent from './components/BirthPeriodListContent';
import { Stack } from '@mantine/core';
import { useBirthObservations } from './hooks/useBirthObservations';
import BirthMeasurementsListContent from './components/BirthMeasurementsListContent';
// -----------------------------------------------------------------------------
const MedicalHistory: FC<SummaryTabBlockChildProps> = ({
  minimizedView = false,
  setCanCloseRef,
  mode,
}) => {
  const patient = usePatient();

  const [isDirty, setIsDirty] = useState(false);

  const {
    questionnaire: prenatalPeriodQuestionnaire,
    questionnaireResponse: prenatalPeriodResponse,
    loading: prenatalPeriodLoading,
  } = usePrenatalPeriodResponse(patient);

  const {
    questionnaire: perinatalPeriodQuestionnaire,
    questionnaireResponse: perinatalPeriodResponse,
    loading: perinatalPeriodLoading,
  } = usePerinatalPeriodResponse(patient);

  const { data: birthObservations, loading: birthObservationsLoading } =
    useBirthObservations(patient);

  const loading =
    prenatalPeriodLoading || perinatalPeriodLoading || birthObservationsLoading;

  useEffect(() => {
    setCanCloseRef?.(!isDirty);
  }, [isDirty, setCanCloseRef]);

  const prenatalHasData = useMemo(
    () =>
      prenatalPeriodResponse?.status === 'completed' ||
      prenatalPeriodResponse?.status === 'in-progress',
    [prenatalPeriodResponse?.status]
  );

  const perinatalHasData = useMemo(
    () =>
      perinatalPeriodResponse?.status === 'completed' ||
      perinatalPeriodResponse?.status === 'in-progress',
    [perinatalPeriodResponse?.status]
  );

  //----------------------------------------------------------------------------
  const renderMinimizedView = () => {
    if (!patient) {
      return null;
    }

    return (
      <Stack gap="xs">
        <BirthPeriodListContent
          periodType="prenatalPeriod"
          questionnaire={prenatalPeriodQuestionnaire}
          questionnaireResponse={prenatalPeriodResponse}
          hasData={prenatalHasData}
          readonly
        />
        <BirthPeriodListContent
          periodType="perinatalPeriod"
          questionnaire={perinatalPeriodQuestionnaire}
          questionnaireResponse={perinatalPeriodResponse}
          hasData={perinatalHasData}
          readonly
        />
        <BirthMeasurementsListContent data={birthObservations} readonly />
      </Stack>
    );
  };

  //----------------------------------------------------------------------------
  const renderFullView = () => {
    if (!patient) {
      return;
    }

    return (
      <MedicalHistoryContent
        patient={patient}
        setIsDirty={setIsDirty}
        readonly={mode === 'view'}
      />
    );
  };

  //----------------------------------------------------------------------------
  return (
    <LoaderWithChildren isLoading={loading}>
      {minimizedView ? renderMinimizedView() : renderFullView()}
    </LoaderWithChildren>
  );
};

export default MedicalHistory;
